/**
 * Хелперы раздела «MLBB трансляции»: подписи, fallback-значения, счёт серии, лента событий.
 * Без зависимостей от Vue — покрыто тестами в tests/unit/mlbbBroadcast.spec.js.
 */
import {heroName} from '@/constants/mlbbHeroes'

export const BATTLE_STATE_LABELS = {
    init: 'Лобби',
    ban: 'Баны',
    pick: 'Пики',
    adjust: 'Подготовка',
    loading: 'Загрузка',
    play: 'Игра',
    end: 'Завершена',
}

export const BATTLE_STATE_VARIANTS = {
    init: 'secondary',
    ban: 'warning',
    pick: 'warning',
    adjust: 'info',
    loading: 'info',
    play: 'success',
    end: 'dark',
}

// Фильтр списка по фазам: в API state одиночный, поэтому группируем на фронте
export const STATE_FILTERS = [
    {value: '', text: 'Все состояния'},
    {value: 'draft', text: 'Драфт', states: ['init', 'ban', 'pick', 'adjust']},
    {value: 'play', text: 'Игра', states: ['loading', 'play']},
    {value: 'end', text: 'Завершены', states: ['end']},
]

export const TOWER_LABELS = {
    up_outer: 'верх, внешняя',
    up_mid: 'верх, средняя',
    up_inner: 'верх, внутренняя',
    mid_outer: 'центр, внешняя',
    mid_mid: 'центр, средняя',
    mid_inner: 'центр, внутренняя',
    down_outer: 'низ, внешняя',
    down_mid: 'низ, средняя',
    down_inner: 'низ, внутренняя',
}

const KILL_BADGES = [
    ['first_blood', 'First blood'],
    ['double_kill', 'Double kill'],
    ['triple_kill', 'Triple kill'],
    ['quadra_kill', 'Quadra kill'],
    ['penta_kill', 'Penta kill'],
    ['legendary', 'Legendary'],
    ['steal', 'Steal'],
]

// Устаревшие типы, API шлёт их вместе с новыми kill_* — дубли выкидываем
const LEGACY_EVENT_TYPES = ['first_blood', 'triple_kill', 'quadra_kill', 'penta_kill',
    'camp_kill_lord', 'camp_kill_tower', 'camp_kill_hero']

// Порог «данные задерживаются» из доки бэка
export const STALE_AFTER_MS = 30 * 1000

export const RECENT_JUDGES_KEY = 'mlbb_broadcast_recent_judges'
const RECENT_JUDGES_LIMIT = 6

export function stateLabel(state) {
    return BATTLE_STATE_LABELS[state] || state || '—'
}

export function stateVariant(state) {
    return BATTLE_STATE_VARIANTS[state] || 'secondary'
}

export function matchesStateFilter(battle, filter) {
    if (!filter) return true
    const option = STATE_FILTERS.find(({value}) => value === filter)
    return !option || !option.states || option.states.includes(battle?.state)
}

/** Секунды → «мм:сс». Ноль — это «00:00», а не пусто */
export function formatGameTime(seconds) {
    const total = Math.max(0, Math.floor(Number(seconds) || 0))
    const pad = (n) => String(n).padStart(2, '0')
    return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`
}

/** Число для таблиц: 0 показываем как 0, отсутствие — как «—» */
export function formatNumber(value) {
    if (value === null || value === undefined || value === '') return '—'
    const n = Number(value)
    if (Number.isNaN(n)) return '—'
    return n.toLocaleString('ru-RU')
}

/** «5 сек назад» / «3 мин назад»; now передаётся снаружи, чтобы тикать без перезапроса */
export function timeAgo(iso, now = Date.now()) {
    if (!iso) return '—'
    const ts = new Date(iso).getTime()
    if (Number.isNaN(ts)) return '—'
    const sec = Math.max(0, Math.round((now - ts) / 1000))
    if (sec < 5) return 'только что'
    if (sec < 60) return `${sec} сек назад`
    if (sec < 3600) return `${Math.round(sec / 60)} мин назад`
    if (sec < 86400) return `${Math.round(sec / 3600)} ч назад`
    return new Date(ts).toLocaleString('ru-RU', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'})
}

export function formatDateTime(iso) {
    if (!iso) return '—'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleString('ru-RU', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'})
}

/* ---------- стороны и команды ---------- */

export function campByNumber(battle, camp) {
    return (battle?.camps || []).find((c) => c.camp === camp) || null
}

/**
 * Название команды стороны: наша команда → игровое имя Moonton → «Синие/Красные».
 * Пока battle не привязан к матчу, team = null и берём in_game_team_name.
 */
export function campTeamName(camp) {
    if (!camp) return '—'
    return camp.team?.name
        || camp.in_game_team_name
        || camp.in_game_team_short_name
        || (camp.camp === 1 ? 'Синие' : 'Красные')
}

export function campShortName(camp) {
    if (!camp) return '—'
    return camp.in_game_team_short_name || camp.in_game_team_name || camp.team?.name || (camp.camp === 1 ? 'BLUE' : 'RED')
}

/** URL логотипа из стандартного объекта лого (original_url / webp / small_webp) */
export function logoUrl(logo) {
    if (!logo) return null
    if (typeof logo === 'string') return logo
    return logo.small_webp || logo.webp || logo.original_url || logo.url || null
}

export function campLogoUrl(camp) {
    return logoUrl(camp?.team?.logo)
}

export function battleTitle(battle) {
    if (!battle) return '—'
    return `${campTeamName(campByNumber(battle, 1))} — ${campTeamName(campByNumber(battle, 2))}`
}

export function isLinked(battle) {
    return !!battle?.group_id
}

/* ---------- игроки ---------- */

/** Игроки стороны в порядке слотов комнаты (pos 1–5 синие, 6–10 красные) */
export function campPlayers(battle, camp) {
    return (battle?.players || [])
        .filter((p) => p.camp === camp)
        .slice()
        .sort((a, b) => (a.pos || 0) - (b.pos || 0))
}

/**
 * Ник игрока: профиль BIGPLAY → ник из игры.
 * linked = false — игрок не найден в наших профилях, в интерфейсе показываем пометку.
 */
export function playerName(player) {
    if (!player) return {name: '—', linked: false}
    const username = player.user?.username
    if (username) return {name: username, linked: true}
    return {name: player.name || `Слот ${player.pos || '?'}`, linked: false}
}

// Справочник героев с бэка по игровому hero_id; заполняет services/mlbbHeroCatalog
export const heroCatalog = {byId: {}}

// Аватар героя: в battle по доке строка, в справочнике — стандартный объект картинки
export function heroAvatarUrl(avatar) {
    if (!avatar) return null
    if (typeof avatar === 'string') return avatar
    return avatar.webp || avatar.original_url || avatar.small_webp || null
}

export function setHeroCatalog(list) {
    const byId = {}
    ;(list || []).forEach((h) => {
        if (h?.hero_id) byId[h.hero_id] = {name: h.name, avatar: heroAvatarUrl(h.avatar)}
    })
    heroCatalog.byId = byId
}

// Герой без объекта hero в battle: справочник бэка, пока он не загрузился — имя из локальной таблицы
function dictionaryHero(heroId) {
    const hero = heroCatalog.byId[heroId]
    if (hero) return {name: hero.name, avatar: hero.avatar, known: true}
    const name = heroName(heroId)
    return name ? {name, avatar: null, known: true} : null
}

/** Герой: из нашего справочника, иначе заглушка с игровым ID (или null, если ещё не выбран) */
export function playerHero(player) {
    if (!player) return null
    if (player.hero) {
        const avatar = heroAvatarUrl(player.hero.avatar) || dictionaryHero(player.hero_id)?.avatar || null
        return {name: player.hero.name, avatar, known: true}
    }
    if (player.hero_id) return dictionaryHero(player.hero_id) || {name: `Герой #${player.hero_id}`, avatar: null, known: false}
    return null
}

/**
 * Герой по игровому ID для банов/пиков в camps: сначала у игрока battle, который взял
 * этого героя, иначе из справочника.
 */
export function heroById(battle, heroId) {
    if (!heroId) return null
    const owner = (battle?.players || []).find((p) => p.hero_id === heroId && p.hero)
    if (owner) return playerHero(owner)
    return dictionaryHero(heroId) || {name: `#${heroId}`, avatar: null, known: false}
}

export function playerKda(player) {
    const n = (v) => Number(v) || 0
    return `${n(player?.kills)} / ${n(player?.deaths)} / ${n(player?.assists)}`
}

export function playerByRoleId(battle, roleId) {
    if (!roleId) return null
    return (battle?.players || []).find((p) => p.role_id === roleId) || null
}

/* ---------- синхронизация ---------- */

/** «Данные задерживаются»: игра не закончена, а live-состояние не обновлялось дольше порога */
export function isStale(battle, now = Date.now()) {
    if (!battle || battle.state === 'end' || !battle.live_synced_at) return false
    const ts = new Date(battle.live_synced_at).getTime()
    if (Number.isNaN(ts)) return false
    return now - ts > STALE_AFTER_MS
}

/**
 * Статус синхронизации battle с Moonton.
 * level: ok | warn | error | done — для цвета точки; text — подпись.
 */
export function syncStatus(battle, now = Date.now()) {
    if (!battle) return {level: 'warn', text: 'Нет данных'}
    if (battle.poll_enabled === false) {
        return {level: 'error', text: battle.last_sync_error ? 'Опрос остановлен из-за ошибки' : 'Опрос остановлен'}
    }
    if (battle.last_sync_error) return {level: 'error', text: 'Ошибка синхронизации'}
    if (battle.state === 'end') {
        return battle.post_synced_at
            ? {level: 'done', text: 'Итог получен'}
            : {level: 'warn', text: 'Ждём итог игры'}
    }
    if (isStale(battle, now)) return {level: 'warn', text: 'Данные задерживаются'}
    return {level: 'ok', text: 'Синхронизируется'}
}

/** Время последнего успешного обновления от Moonton */
export function lastUpdateAt(battle) {
    if (!battle) return null
    const candidates = [battle.live_synced_at, battle.post_synced_at, battle.last_polled_at]
        .filter(Boolean)
        .map((iso) => ({iso, ts: new Date(iso).getTime()}))
        .filter(({ts}) => !Number.isNaN(ts))
    if (!candidates.length) return null
    return candidates.sort((a, b) => b.ts - a.ts)[0].iso
}

/**
 * Слить новое состояние battle со старым. WS (mlbb-battle.updated) шлёт клиентский объект без
 * служебных полей опроса (poll_enabled, last_sync_error, ...) — их сохраняем из админского ответа.
 */
export function mergeBattle(prev, next) {
    if (!next) return prev
    if (!prev || prev.id !== next.id) return next
    return {...prev, ...next}
}

/* ---------- серия ---------- */

/** Счёт серии по завершённым играм: {[group_team_id]: побед} */
export function seriesScore(battles) {
    const score = {}
    ;(battles || []).forEach((b) => {
        if (b.state !== 'end' || !b.win_camp) return
        const winner = b.winner_group_team_id || campByNumber(b, b.win_camp)?.group_team_id
        if (!winner) return
        score[winner] = (score[winner] || 0) + 1
    })
    return score
}

/** Текущая игра серии: последняя не завершённая, иначе последняя */
export function currentSeriesBattle(battles) {
    const list = battles || []
    const running = list.filter((b) => b.state !== 'end')
    return running[running.length - 1] || list[list.length - 1] || null
}

/* ---------- лента событий ---------- */

function eventKey(e) {
    return e.id !== undefined && e.id !== null ? `id:${e.id}` : `${e.event_type}:${e.game_time}:${e.camp}:${e.killer_role_id || ''}:${e.killed_role_id || ''}`
}

/**
 * Добавить события в ленту: без дублей (REST-догрузка после реконнекта пересекается с WS),
 * без устаревших типов, если на ту же секунду уже есть новый kill_*. Сортировка по game_time.
 */
export function mergeEvents(current, incoming) {
    const map = new Map()
    ;[...(current || []), ...(incoming || [])].forEach((e) => {
        if (e) map.set(eventKey(e), e)
    })
    const all = [...map.values()]
    const modernTimes = new Set(all.filter((e) => !LEGACY_EVENT_TYPES.includes(e.event_type)).map((e) => e.game_time))
    return all
        .filter((e) => !LEGACY_EVENT_TYPES.includes(e.event_type) || !modernTimes.has(e.game_time))
        .sort((a, b) => (a.game_time || 0) - (b.game_time || 0) || (a.id || 0) - (b.id || 0))
}

export function lastEventGameTime(events) {
    return (events || []).reduce((max, e) => Math.max(max, Number(e.game_time) || 0), 0)
}

/* ---------- таймлайн (графики по ходу игры) ---------- */

// Бэк отдаёт точку на каждую секунду — для графика хватает каждой 10-й
export const TIMELINE_STEP = 10

/** Добавить точки таймлайна: одна точка на секунду игрового времени, по возрастанию */
export function mergeTimeline(current, incoming) {
    const map = new Map()
    ;[...(current || []), ...(incoming || [])].forEach((p) => {
        if (p && p.game_time != null) map.set(Number(p.game_time), p)
    })
    return [...map.values()].sort((a, b) => a.game_time - b.game_time)
}

export function lastTimelineGameTime(points) {
    return points && points.length ? Number(points[points.length - 1].game_time) || 0 : 0
}

/**
 * Точка таймлайна из тика mlbb-battle.updated (по доке — game_time и camps[].total_money).
 * Опыта в camps нет, поэтому total_exp / diff.exp = null. Вне фазы play точек нет.
 */
export function timelinePointFromBattle(battle) {
    if (!battle || battle.state !== 'play' || !(battle.game_time > 0)) return null
    const side = (n) => {
        const c = campByNumber(battle, n) || {}
        const v = (key) => Number(c[key]) || 0
        return {
            score: v('score'),
            total_money: v('total_money'),
            total_exp: null,
            kill_lord: v('kill_lord'),
            kill_tortoise: v('kill_tortoise'),
            kill_tower: v('kill_tower'),
        }
    }
    const blue = side(1)
    const red = side(2)
    return {
        game_time: battle.game_time,
        state: battle.state,
        blue,
        red,
        diff: {
            gold: blue.total_money - red.total_money,
            exp: null,
            kills: blue.score - red.score,
            towers: blue.kill_tower - red.kill_tower,
        },
    }
}

function eventBadges(e) {
    const extra = e.extra_params
    const keys = Array.isArray(extra) ? extra : Object.keys(extra || {}).filter((k) => extra[k])
    if (e.event_type === 'first_blood' && !keys.includes('first_blood')) keys.push('first_blood')
    return KILL_BADGES.filter(([key]) => keys.includes(key)).map(([, label]) => label)
}

/** Человекочитаемое описание события для ленты */
export function describeEvent(e, battle) {
    const who = (roleId) => {
        const p = playerByRoleId(battle, roleId)
        if (!p) return null
        const hero = playerHero(p)
        return `${playerName(p).name}${hero ? ` (${hero.name})` : ''}`
    }
    const team = (camp) => campTeamName(campByNumber(battle, camp)) || (camp === 1 ? 'Синие' : 'Красные')
    let text
    switch (e.event_type) {
        case 'kill_hero': {
            const killer = who(e.killer_role_id) || e.killer_name || team(e.killer_camp || e.camp)
            const killed = who(e.killed_role_id) || e.killed_name || 'героя'
            text = `${killer} убил ${killed}`
            break
        }
        case 'kill_boss':
            text = `${team(e.camp)} забрали ${e.boss_name === 'lord' ? 'Лорда' : e.boss_name === 'tortoise' ? 'Черепаху' : 'босса'}`
            break
        case 'kill_tower':
            text = `${team(e.camp)} снесли башню${e.tower_name ? ` (${TOWER_LABELS[e.tower_name] || e.tower_name})` : ''}`
            break
        case 'camp_ace':
            text = `${team(e.camp)} — вайп соперника`
            break
        case 'pause':
            text = 'Пауза'
            break
        case 'resume':
            text = 'Игра продолжена'
            break
        default:
            text = e.event_type || 'Событие'
    }
    return {text, badges: eventBadges(e), camp: e.camp || null}
}

/* ---------- недавние ID судей (только в этом браузере) ---------- */

export function readRecentJudges(storage = window.localStorage) {
    try {
        const list = JSON.parse(storage.getItem(RECENT_JUDGES_KEY) || '[]')
        return Array.isArray(list) ? list.filter((id) => Number.isInteger(id) && id > 0) : []
    } catch (e) {
        return []
    }
}

export function pushRecentJudge(judgeId, storage = window.localStorage) {
    const id = Number(judgeId)
    if (!Number.isInteger(id) || id <= 0) return readRecentJudges(storage)
    const list = [id, ...readRecentJudges(storage).filter((x) => x !== id)].slice(0, RECENT_JUDGES_LIMIT)
    try {
        storage.setItem(RECENT_JUDGES_KEY, JSON.stringify(list))
    } catch (e) {
        // приватный режим / переполнение — недавние просто не запомнятся
    }
    return list
}

/** Разобрать ввод ID судьи: только цифры, иначе null */
export function parseJudgeId(value) {
    const digits = String(value ?? '').trim()
    if (!/^\d+$/.test(digits)) return null
    const id = Number(digits)
    return Number.isSafeInteger(id) && id > 0 ? id : null
}
